import React from "react";
import { Loader2, Save, Trash2, Plus } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { rowKey } from "@/lib/rowKey";
import { inputClass, labelClass, btnPrimary, btnGhost, iconBtnClass } from "@/components/Brand";

/** Create / edit a single project preset. */
export const TemplateForm = ({ form, setForm, editing, busy, onSubmit, onCancel, onQuota }) => {
  const { t } = useI18n();

  return (
    <form onSubmit={onSubmit} className="space-y-6" data-testid="template-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div>
          <label className={labelClass}>{t("template_name")}</label>
          <input data-testid="template-name-input" className={inputClass} value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Paket Combo 7 Edit" required />
        </div>
        <div>
          <label className={labelClass}>{t("edit_quota")}</label>
          <input data-testid="template-edit-quota-input" type="number" min="1" className={inputClass}
            value={form.edit_quota} onChange={(e) => setForm({ ...form, edit_quota: e.target.value })} />
        </div>
        <div>
          <label className={labelClass}>{t("max_extra")}</label>
          <input data-testid="template-max-extra-input" type="number" min="0" className={inputClass}
            value={form.max_extra_edits} onChange={(e) => setForm({ ...form, max_extra_edits: e.target.value })} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label className={labelClass}>{t("extra_price_override")}</label>
          <input data-testid="template-extra-price-input" type="number" className={inputClass}
            value={form.extra_edit_price} onChange={(e) => setForm({ ...form, extra_edit_price: e.target.value })}
            placeholder="5000" />
        </div>
        <div>
          <label className={labelClass}>{t("print_sizes")}</label>
          <input data-testid="template-print-sizes-input" className={inputClass} value={form.print_sizes}
            onChange={(e) => setForm({ ...form, print_sizes: e.target.value })}
            placeholder="8x12 in, 12x18 in (SC)" />
        </div>
      </div>
      <div>
        <label className={labelClass}>{t("package_price")}</label>
        <input data-testid="template-package-price-input" type="number" min="0" className={inputClass}
          value={form.package_price} onChange={(e) => setForm({ ...form, package_price: e.target.value })}
          placeholder="750000" />
        <p className="mt-2 text-[11px] text-[#707070]">{t("package_price_hint")}</p>
      </div>
      <div>
        <label className={labelClass}>{t("print_quotas")}</label>
        <div className="space-y-3 mt-3">
          {form.print_quota_items.map((item, idx) => (
            <div key={item._k} className="grid grid-cols-[1fr_5rem_1.5rem] gap-3 items-center">
              <input data-testid={`template-quota-size-${idx}`} className={inputClass} value={item.size}
                onChange={(e) => onQuota(idx, { size: e.target.value })} placeholder="12x18 in" />
              <input data-testid={`template-quota-qty-${idx}`} type="number" min="0" className={inputClass}
                value={item.qty} onChange={(e) => onQuota(idx, { qty: e.target.value })} />
              <button type="button" data-testid={`template-quota-remove-${idx}`}
                onClick={() => setForm((f) => ({
                  ...f, print_quota_items: f.print_quota_items.filter((_, n) => n !== idx),
                }))}
                className={`${iconBtnClass} text-[#5A5A5A] hover:text-[#8B0000]`}
                aria-label={t("remove")}><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <button type="button" data-testid="template-add-quota-btn"
          onClick={() => setForm((f) => ({ ...f, print_quota_items: [...f.print_quota_items, { size: "", qty: 1, _k: rowKey() }] }))}
          className="mt-4 inline-flex items-center min-h-[44px] text-[10px] tracking-[0.18em] uppercase text-[#D4AF37] hover:underline">
          <Plus size={12} className="inline" /> {t("add_size")}
        </button>
      </div>
      <div className="flex flex-wrap gap-3">
        <button data-testid="save-template-btn" disabled={busy} className={btnPrimary}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {editing ? t("save_changes") : t("add_template")}
        </button>
        {editing && (
          <button type="button" data-testid="cancel-template-edit-btn" onClick={onCancel} className={btnGhost}>
            {t("cancel")}
          </button>
        )}
      </div>
    </form>
  );
};
